import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
const ANONYMOUS = { status: "anonymous", user: null, session: null };
const SESSION_LOST_CODES = ["session_invalid", "session_expired", "account_disabled"];

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: "loading", user: null, session: null });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setState({ status: "authenticated", user: data.user, session: data.session });
      return data;
    } catch {
      setState(ANONYMOUS);
      return null;
    }
  }, []);

  const applyAuth = useCallback((data) => {
    setState({ status: "authenticated", user: data.user, session: data.session });
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setState(ANONYMOUS);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const id = api.interceptors.response.use(
      (response) => response,
      (error) => {
        const code = error?.response?.data?.detail?.code;
        if (error?.response?.status === 401 && SESSION_LOST_CODES.includes(code)) {
          setState((current) => (current.status === "authenticated" ? ANONYMOUS : current));
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const value = useMemo(() => ({ ...state, refresh, applyAuth, signOut }), [state, refresh, applyAuth, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function homeFor(user) {
  // Server-decided destination (ADR-003/ADR-016). The user_type fallback only covers a missing payload.
  return user?.home || (user?.user_type === "staff" ? "/hub" : "/account");
}

export function hasCapability(user, code) {
  return Array.isArray(user?.capabilities) && user.capabilities.includes(code);
}

export function hasRole(user, code) {
  return Array.isArray(user?.roles) && user.roles.includes(code);
}

export function routeForAuth(data) {
  if (data?.status === "mfa_required") return "/mfa";
  if (data?.status === "mfa_enrolment_required") return "/mfa/setup";
  return homeFor(data?.user);
}
